import React from "react";
import { Box, Typography } from "@mui/material";
import ResourceLabel from "../calendar/ResourceLabel";
import CalendarSlot1 from "./CalendarSlot1";
import CalendarSlot2 from "./CalendarSlot2";
import { slotStatus } from "./Slot";
import { status } from "shared/src/appointment.mjs";
import { serviceFormat } from "./fn.mjs";
import { groupBy } from "shared/src/util.mjs";
import * as api from "../../services/api";

/**
 * @param {Appointment[]} appointments
 *
 * @returns {ResourceStatus}
 */
function appointmentsStatus(appointments) {
  const statuses = new Set(appointments.map((a) => a.status_id));

  if (statuses.has(status.ASSIGNED) || statuses.has(status.WAITING)) {
    return {
      type: slotStatus.WAITING,
      appointments,
    };
  }

  if (statuses.has(status.TREATING)) {
    return {
      type: slotStatus.TREATING,
      appointments,
    };
  }

  if (statuses.has(status.TREATED)) {
    return {
      type: slotStatus.TREATED,
      appointments,
    };
  }
}

const statusEmpty = { type: slotStatus.EMPTY };
const statusLocked = { type: slotStatus.LOCKED };

/**
 * @param {Appointment[]} appointments
 *
 * @returns {ResourceStatus[]} Array of size 2
 */
function resourceStatus(appointments) {
  const appointmentsByGroupId = groupBy(
    appointments,
    (appointment) => appointment.group_id ?? appointment.id,
  );
  const groupOrder = Object.keys(appointmentsByGroupId);

  const statuses = groupOrder.map((g) =>
    appointmentsStatus(appointmentsByGroupId[g]),
  );

  for (let i = statuses.length; i < 2; i++) {
    const prevStatus = statuses[i - 1];
    if (prevStatus == null || prevStatus.type === slotStatus.TREATED) {
      statuses.push(statusEmpty);

      continue;
    }

    statuses.push(statusLocked);
  }

  statuses.splice(2);

  return statuses;
}

/**
 * @param {DataTransfer} dataTransfer
 * @param {Set<string>} resourceGroups
 */
function hasResourceGroup(dataTransfer, resourceGroups) {
  const types = new Set(dataTransfer.types);

  return (
    Array.from(resourceGroups)
      .map((g) => serviceFormat(g))
      .find((v) => types.has(v)) != null
  );
}

/**
 * @param {ResourceStatus[]} statuses
 */
function isCalendarAvailable(e, statuses, resourceGroups) {
  return (
    statuses.some((s) => s.type === slotStatus.EMPTY) ||
    hasResourceGroup(e.dataTransfer, resourceGroups)
  );
}

/**
 * @param {DataTransfer} dataTransfer
 * @param {import("./types").Resource} resource
 *
 * @returns {string=}
 */
function getServiceTransferFormat(dataTransfer, resource) {
  const types = new Set(dataTransfer.types);

  return [resource.service_type, resource.id]
    .map((v) => serviceFormat(v))
    .find((v) => types.has(v));
}

function isValidDraggable(e, resource) {
  return getServiceTransferFormat(e.dataTransfer, resource) != null;
}

export default function Resource({
  resource,
  setAssignCalendar,
  setEditEvent,
  appointments,
  setChangeEmployee,
}) {
  const statuses = resourceStatus(appointments);

  const appointments1 = statuses[0]?.appointments ?? [];
  const appointments2 = statuses[1]?.appointments ?? [];
  const resourceGroups = new Set(
    [...appointments, ...appointments2].map((a) => a.group_id),
  );
  resourceGroups.delete(null);

  const dragnAndDropProps = React.useMemo(() => {
    if (setAssignCalendar == null) {
      return;
    }

    return {
      onDragEnter: function (e) {
        const el = e.currentTarget;
        if (
          isValidDraggable(e, resource) &&
          isCalendarAvailable(e, statuses, resourceGroups)
        ) {
          el.classList.add("dropping");
        }
      },
      onDragLeave: function (e) {
        if (e.target !== e.currentTarget) {
          return;
        }
        const el = e.currentTarget;
        el.classList.remove("dropping");
      },
      onDragOver: function (e) {
        if (
          isValidDraggable(e, resource) &&
          isCalendarAvailable(e, statuses, resourceGroups)
        ) {
          e.preventDefault();
        }
      },
      onDrop: function (e) {
        const el = e.currentTarget;
        el.classList.remove("dropping");

        const { appointment_ids, appointment_group_id } = JSON.parse(
          e.dataTransfer.getData(
            getServiceTransferFormat(e.dataTransfer, resource),
          ),
        );

        if (resourceGroups.has(appointment_group_id)) {
          const relatedGroupAppointment = appointments.find(
            (a) => a.group_id === appointment_group_id,
          );

          if (relatedGroupAppointment != null) {
            api.patchAppointments(
              appointment_ids.map((id) => ({
                id: id,
                employee_id: relatedGroupAppointment.employee_id,
                actual_calendar_id: relatedGroupAppointment.actual_calendar_id,
                status_id: relatedGroupAppointment.status_id,
              })),
            );

            return;
          }
        }

        setAssignCalendar({
          appointment_ids: appointment_ids,
          actual_calendar: resource,
        });
      },
    };
  }, [setAssignCalendar, resource, statuses]);

  return (
    <Box
      className="floor-manager-resource"
      sx={{ display: "flex", flexShrink: 1, flexGrow: 1 }}
      {...dragnAndDropProps}
    >
      <Box sx={{ flexGrow: 1, flexShrink: 1, width: 0, position: "relative" }}>
        <Typography variant="h3" sx={{ ml: "1.6rem" }}>
          {resource.title}
        </Typography>
        {appointments1.length !== 0 && (
          <ResourceLabel appointmentStep={appointments1[0].active_step} />
        )}
        <CalendarSlot1
          appointments={appointments1}
          status={statuses[0].type}
          setChangeEmployee={setChangeEmployee}
          setEditEvent={setEditEvent}
        />
        <CalendarSlot2
          appointments={appointments2}
          status={statuses[1].type}
          setChangeEmployee={setChangeEmployee}
          setEditEvent={setEditEvent}
        />
      </Box>
    </Box>
  );
}
